import { BoardType, DirectionType, TileType } from '../types';
import { 
  GRID_SIZE, 
  cloneBoard, 
  getDirectionVectors, 
  getTraversalOrder, 
  findFarthestPosition
} from './boardUtils';
import { v4 as uuidv4 } from 'uuid';

// Move all tiles in the specified direction and merge where possible
export function moveTiles(
  board: BoardType,
  direction: DirectionType
): { newBoard: BoardType, scoreGain: number, moved: boolean } {
  const newBoard = cloneBoard(board);
  let scoreGain = 0;
  let moved = false;
  
  // Reset tile states
  for (let y = 0; y < GRID_SIZE; y++) {
    for (let x = 0; x < GRID_SIZE; x++) {
      if (newBoard[y][x]) {
        newBoard[y][x] = {
          ...newBoard[y][x]!,
          isNew: false,
          isMerged: false,
          position: { x, y }
        };
      }
    }
  }

  // Get movement vector and traversal order based on direction
  const { dx, dy } = getDirectionVectors(direction);
  const { x: traversalX, y: traversalY } = getTraversalOrder(direction);
  
  // Process each cell in the correct traversal order
  traversalY.forEach(y => {
    traversalX.forEach(x => {
      const currentTile = newBoard[y][x];
      if (!currentTile) return;
      
      // Find the farthest position and next tile
      const { farthest, next } = findFarthestPosition(newBoard, x, y, dx, dy);
      
      // If we can't move or there's no next tile, do nothing
      if (farthest.x === x && farthest.y === y && !next) return;
      
      // Move to farthest empty position
      if (farthest.x !== x || farthest.y !== y) {
        newBoard[farthest.y][farthest.x] = {
          ...currentTile,
          position: { x: farthest.x, y: farthest.y }
        };
        newBoard[y][x] = null;
        moved = true;
      }
      
      // Merge with next tile if values match
      if (next && newBoard[next.y][next.x]?.value === currentTile.value && !newBoard[next.y][next.x]?.isMerged) {
        const mergedValue = currentTile.value * 2;
        scoreGain += mergedValue;
        
        newBoard[next.y][next.x] = {
          value: mergedValue,
          id: uuidv4(),
          isMerged: true,
          position: { x: next.x, y: next.y }
        };
        
        newBoard[farthest.y][farthest.x] = null;
        moved = true;
      }
    });
  });
  
  // Return new board state, score gain, and whether any tiles moved
  return { newBoard, scoreGain, moved };
}